import React from 'react';
import { Users, AlertCircle, Info } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/Card';
import type { ProfilePersonaView } from '../../../api/PublicProfile/publicProfileApi';

interface PersonaSelectorProps {
  personas: ProfilePersonaView[];
  selectedPersona: number | null;
  selectedPersonaData: ProfilePersonaView | undefined;
  onPersonaChange: (personaId: number) => void;
  t: (key: string) => string;
}

const PersonaSelector: React.FC<PersonaSelectorProps> = ({
  personas,
  selectedPersona,
  selectedPersonaData,
  onPersonaChange,
  t
}) => {
  return (
    <Card className="mb-6">
      <CardContent className="p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Users className="w-5 h-5 me-2" />
          {t('Public Profile Persona')}
        </h2>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('Select Persona')}
            </label>
            <select
              value={selectedPersona ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value) {
                  onPersonaChange(Number(value));
                }
              }}
              className="w-full px-3 py-2 border border-input rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
            >
              {!selectedPersona && (
                <option value="">{t('-- Select a persona --')}</option>
              )}
              {personas.map(persona => (
                <option key={persona.id} value={persona.id}>
                  {persona.name} {persona.isDefault ? `(${t('Default')})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Display selected persona details */}
          {selectedPersonaData && (
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-foreground">
                  {selectedPersonaData.name}
                </p>
                <div className="flex gap-2">
                  {selectedPersonaData.requiresApproval && (
                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-warning/20 text-warning-foreground">
                      {t('Requires Approval')}
                    </span>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {selectedPersonaData.description || t('No description')}
              </p>
            </div>
          )}

          {/* No personas available message */}
          {personas.length === 0 && (
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
              <p className="text-sm text-foreground">
                <AlertCircle className="w-4 h-4 inline me-2" />
                {t('No personas available. Please contact your HR administrator to set up profile personas.')}
              </p>
            </div>
          )}

          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Info className="w-3 h-3" />
            {t('All configuration settings below apply to the selected persona')}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default PersonaSelector;
